import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5555';

interface PromoCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPromoApplied: (code: string) => void;
}

const EARLY_BIRD_CODE = 'EarlyBird';

const APPROVAL_STEPS = [
  'Verifying eligibility...',
  'Checking available promotions...',
  'Generating your personal code...',
  'Applying early bird discount...',
];

const PromoCodeModal: React.FC<PromoCodeModalProps> = ({ isOpen, onClose, onPromoApplied }) => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isApproved, setIsApproved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [customCodeError, setCustomCodeError] = useState('');
  const [validatingCustom, setValidatingCustom] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setIsLoading(false);
      setCurrentStep(0);
      setIsApproved(false);
      setCopied(false);
      setShowCustomInput(false);
      setCustomCode('');
      setCustomCodeError('');
      setValidatingCustom(false);
    }
  }, [isOpen]);

  const handleRequestPromo = () => {
    setIsLoading(true);
    setCurrentStep(0);

    // Simulate approval process with steps
    const stepDuration = 800;
    APPROVAL_STEPS.forEach((_, index) => {
      setTimeout(() => {
        setCurrentStep(index + 1);
        if (index === APPROVAL_STEPS.length - 1) {
          setTimeout(() => {
            setIsLoading(false);
            setIsApproved(true);
          }, stepDuration);
        }
      }, stepDuration * (index + 1));
    });
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(EARLY_BIRD_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleApplyCode = () => {
    onPromoApplied(EARLY_BIRD_CODE);
    onClose();
  };

  const handleValidateCustomCode = async () => {
    if (!customCode.trim()) {
      setCustomCodeError('Please enter a promo code');
      return;
    }

    setValidatingCustom(true);
    setCustomCodeError('');

    try {
      const response = await fetch(`${API_BASE}/api/subscriptions/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promoCode: customCode.trim(),
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
        }),
      });

      const data = await response.json();

      if (data.valid) {
        onPromoApplied(customCode.trim());
        onClose();
      } else {
        setCustomCodeError(data.message || 'Invalid promo code');
      }
    } catch (error) {
      console.error('Failed to validate promo code:', error);
      setCustomCodeError('Failed to validate promo code');
    } finally {
      setValidatingCustom(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {isApproved ? '🎉 Congratulations!' : '🎁 Special Offer'}
            </h2>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!isLoading && !isApproved && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🏷️</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Want 40% Off?
                </h3>
                <p className="text-gray-600">
                  As an early supporter, you're eligible for our exclusive Early Bird discount.
                  Request your promo code now!
                </p>
              </div>

              <button
                onClick={handleRequestPromo}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                🚀 Fast Request Promo Code
              </button>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowCustomInput(!showCustomInput)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {showCustomInput ? 'Hide' : 'Have a promo code? Enter it here'}
                </button>

                {showCustomInput && (
                  <div className="mt-3 space-y-2">
                    <input
                      type="text"
                      value={customCode}
                      onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                      placeholder="Enter promo code"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    />
                    {customCodeError && (
                      <p className="text-sm text-red-600">{customCodeError}</p>
                    )}
                    <button
                      onClick={handleValidateCustomCode}
                      disabled={validatingCustom}
                      className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {validatingCustom ? 'Validating...' : 'Apply Code'}
                    </button>
                  </div>
                )}
              </div>

              <p className="text-center text-sm text-gray-500 mt-4">
                Limited time offer • No credit card required
              </p>
            </>
          )}

          {isLoading && (
            <div className="py-8">
              <div className="flex justify-center mb-6">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
              <div className="space-y-3">
                {APPROVAL_STEPS.map((step, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 transition-opacity duration-300 ${
                      index < currentStep ? 'opacity-100' : 'opacity-30'
                    }`}
                  >
                    {index < currentStep ? (
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0"></div>
                    )}
                    <span className={`text-sm ${index < currentStep ? 'text-gray-900' : 'text-gray-500'}`}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isApproved && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  You've Been Approved!
                </h3>
                <p className="text-gray-600">
                  You qualify for the Early Bird discount. Use this code to get <strong>40% off</strong> any plan!
                </p>
              </div>

              {/* Promo Code Display */}
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your Promo Code</p>
                    <p className="text-2xl font-bold text-gray-900 font-mono">{EARLY_BIRD_CODE}</p>
                  </div>
                  <button
                    onClick={handleCopyCode}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      copied
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {copied ? (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </span>
                    )}
                  </button>
                </div>
              </div>

              <button
                onClick={handleApplyCode}
                className="w-full py-3 px-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              >
                Apply Code & See Discounted Prices
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoCodeModal;
